import { useState } from "react";
import { Button, Card, Typography, Space, Form, Input, Alert } from "antd";
import { ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "stores/AuthStore/AuthStore";
import {
  renderErrorNotifications,
  renderSuccessNotifications,
} from "helpers/error.helpers";
import { proxyLogin } from "./proxyLogin.service";

const { Title, Text } = Typography;

function SuperAdminLogin() {
  const navigate = useNavigate();
  const setUserData = useAuthStore((s) => s.setUserData);
  const [form] = Form.useForm();
  const [loggingIn, setLoggingIn] = useState(false);

  const handleLogin = async () => {
    try {
      const values = await form.validateFields();
      setLoggingIn(true);
      const { data, errors } = await proxyLogin({
        phone_number: values.phone_number,
        otp: values.otp,
      });
      if (errors?.length > 0) {
        renderErrorNotifications(errors);
        return;
      }

      if (!data?.token && data?.account_info?.account_id) {
        renderSuccessNotifications({
          title: "Account Under Review",
          message: data?.message || "This account is still pending approval.",
        });
        navigate(
          `/application-status?account_id=${data?.account_info?.account_id}`
        );
        return;
      }

      setUserData({ user: data.account_info, token: data.token });
      renderSuccessNotifications({
        title: "Proxy login successful",
        message: `Signed in as ${data?.account_info?.name || data?.account_info?.phone_number}`,
      });
      navigate("/shoup-community/dashboard", { replace: true });
    } catch (err) {
      if (err?.errorFields) return;
      renderErrorNotifications([
        {
          message: err?.message || "Proxy login failed. Check credentials.",
        },
      ]);
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0f0f12",
        padding: 24,
      }}
    >
      <div style={{ width: "100%", maxWidth: 420 }}>
        <Card
          style={{
            borderRadius: 20,
            border: "1px solid #2a2a35",
            background: "#18181f",
          }}
          styles={{ body: { padding: 32 } }}
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: "#1e1e2e",
                  border: "1px solid #2a2a45",
                  marginBottom: 16,
                }}
              >
                <ShieldAlert size={28} color="#7c6af7" />
              </div>
              <Title
                level={3}
                style={{ margin: 0, color: "#e8e8f0", letterSpacing: "-0.02em" }}
              >
                Super Admin Access
              </Title>
              <Text style={{ color: "#6b6b7e", fontSize: 13 }}>
                Proxy login — restricted to authorised staff only.
              </Text>
            </div>

            <Alert
              type="warning"
              showIcon
              message="This page is not linked anywhere in the app. Treat your proxy OTP as a secret."
              style={{ borderRadius: 10, fontSize: 12 }}
            />

            <Form
              form={form}
              layout="vertical"
              onFinish={handleLogin}
              style={{ marginTop: 4 }}
            >
              <Form.Item
                name="phone_number"
                label={<Text style={{ color: "#c0c0d0" }}>Phone number</Text>}
                rules={[
                  { required: true, message: "Phone number is required." },
                  {
                    pattern: /^\d{10}$/,
                    message: "Enter a valid 10-digit phone number.",
                  },
                ]}
              >
                <Input
                  type="tel"
                  inputMode="numeric"
                  placeholder="9999999999"
                  maxLength={10}
                  size="large"
                  style={{ borderRadius: 10 }}
                />
              </Form.Item>

              <Form.Item
                name="otp"
                label={<Text style={{ color: "#c0c0d0" }}>Proxy OTP</Text>}
                rules={[
                  { required: true, message: "Proxy OTP is required." },
                ]}
              >
                <Input.Password
                  placeholder="Enter your proxy OTP"
                  size="large"
                  style={{ borderRadius: 10 }}
                />
              </Form.Item>

              <Button
                type="primary"
                size="large"
                block
                htmlType="submit"
                loading={loggingIn}
                style={{
                  borderRadius: 10,
                  height: 44,
                  background: "#7c6af7",
                  borderColor: "#7c6af7",
                  marginTop: 4,
                }}
              >
                Login as this user
              </Button>
            </Form>
          </Space>
        </Card>
      </div>
    </main>
  );
}

export default SuperAdminLogin;
